import { Div, Button, state, router, IRouteEvent } from '@codesuma/baseline'
import { open } from '../../helpers/panes'
import settings, { Theme } from '../../services/settings'
import calendar, { IDayCell } from '../../services/calendar'
import { Slider, ISliderFill, ISliderMove } from '../../components/slider'
import { MonthSlide } from './month-slide'
import { MonthBackdrop } from './month-backdrop'
import { MenuIcon } from './menu-icon'
import { CalendarToggle } from './calendar-toggle'
import { RatesTicker } from './rates-ticker'
import { CountryIntro, countryIntroDue } from './country-intro'
import theme from '../../services/theme'
import style from './index.module.css'

// The magnifier, after Material's but mirrored: the lens faces right, into the
// calendar from the corner it stands in. The handle is shorter too.
const SEARCH_SVG = '<svg viewBox="2 2 20 20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="13.6" cy="10.4" r="5.5"/><path stroke-width="2.2" d="M9.71 14.29L4.76 19.24"/></svg>'
// Material Design icons (Apache 2.0), all inheriting currentColor.
const MOON_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3a9 9 0 1 0 9 9c0-.46-.04-.92-.1-1.36a5.389 5.389 0 0 1-4.4 2.26 5.403 5.403 0 0 1-3.14-9.8c-.44-.06-.9-.1-1.36-.1z"/></svg>'
const SUN_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2a1 1 0 0 0 0-2H2a1 1 0 0 0 0 2zm18 0h2a1 1 0 0 0 0-2h-2a1 1 0 0 0 0 2zM11 2v2a1 1 0 0 0 2 0V2a1 1 0 0 0-2 0zm0 18v2a1 1 0 0 0 2 0v-2a1 1 0 0 0-2 0zM5.99 4.58a1 1 0 0 0-1.41 1.41l1.06 1.06a1 1 0 0 0 1.41-1.41L5.99 4.58zm12.37 12.37a1 1 0 0 0-1.41 1.41l1.06 1.06a1 1 0 0 0 1.41-1.41l-1.06-1.06zm1.06-9.9a1 1 0 0 0-1.41-1.41l-1.06 1.06a1 1 0 0 0 1.41 1.41l1.06-1.06zM7.05 18.36a1 1 0 0 0-1.41-1.41l-1.06 1.06a1 1 0 0 0 1.41 1.41l1.06-1.06z"/></svg>'
const PALETTE_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zM6.5 12c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>'
// The two themes with no picture: an empty ring for the pale one, the same ring
// filled for the dark. A circle because that is what is left of a scene once
// the scene is gone.
const RING_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>'
const DISC_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/></svg>'

export const HomePage = () => {
    // Base container
    const base = Div()
    base.addClass(style.base)

    // First, so everything else paints over it: the seasonal panorama the
    // months pan across. It is behind the slider and behind the floating
    // controls, and it takes no taps of its own.
    const backdrop = MonthBackdrop()
    base.append(backdrop)

    // The day open in a sheet over the calendar, if one is — see the day view
    // further down. Here because the slides read it as they are built.
    let dayView: IDayCell | null = null
    // Each slide's month, so the one on screen can be found to rise.
    const months = new Map<ISliderFill['slide'], ReturnType<typeof MonthSlide>>()
    // Where the sheet stops: just under the day's row. Said through state,
    // because the sheet is pages/day's and the row is this page's.
    const placeSheet = (top: number | undefined) => {
        if (top !== undefined) state.set('day-sheet-top', top)
    }

    const slider = Slider()
    slider.on('fill', ({ slide, index }: ISliderFill) => {
        // A slide is built from scratch every time anything it draws changes —
        // a setting, a moon-sighting correction, a task ticked off on the month
        // itself. The band under the grid picks up where the last one was left,
        // so ticking something halfway down the list does not throw the reader
        // back to the top of the month. Only when the month is the same one: a
        // slide the slider has wrapped round to the other side holds a
        // different month, and that starts at the top. So does any month while
        // the day's sheet is up: its band belongs at the top then (see the day
        // view below), even if the one being replaced was still on its way.
        const same = slide.el.dataset.month === String(index) && !dayView
        const band = same ? slide.el.querySelector('[data-band]')?.scrollTop || 0 : 0
        // The same goes for the line a day's sheet stands under (see the day
        // view below): the month built in its place lays its own line out from
        // the day the old one had first in view.
        const from = dayView ? months.get(slide)?.first() : undefined
        slide.el.dataset.month = String(index)
        slide.empty()
        const ms = MonthSlide(index)
        months.set(slide, ms)
        // open() rather than router.goto: beside the calendar the day panel is
        // already on /day when a second date is tapped, and the router alone
        // would ignore that. See helpers/panes. With the sheet up over the
        // calendar, a tapped day is handed to the sheet to go to instead.
        ms.on('day-open', (c: IDayCell) => dayView
            ? state.set('day-pick', c)
            : open(`/day?d=${c.gy}-${c.gm}-${c.gd}`))
        ms.on('year-open', (year: number) => router.goto(`/year?y=${year}`))
        // The steppers beside the grid, for everyone who never discovers the swipe.
        ms.on('month-prev', () => slider.prev())
        ms.on('month-next', () => slider.next())
        // The task wizard, the same page the day page's "+ کار" opens, so a
        // task is only ever made in one place. No day in the route: a month is
        // not a day, so the wizard asks for one — see the task page.
        ms.on('add-task', () => router.goto('/task'))
        // A single editor asks for the day and type.
        ms.on('add-to-month', () => router.goto('/event'))
        slide.append(ms)
        // After the append: a band with no layout behind it yet cannot scroll.
        if (band) ms.el.querySelector('[data-band]')!.scrollTop = band
        // A month built while the sheet is up starts out risen under it.
        if (dayView && slide.getPos() === 1) placeSheet(ms.lift(dayView, true, from))
    })
    base.append(slider)

    // Gregorian mode reads LTR, so months also advance in the LTR direction.
    const syncDirection = () =>
        slider.direction(settings.get().system === 'gregorian' ? 'ltr' : 'rtl')
    // The panorama rests on the visible month's frame; a swipe pans between two
    // at half the slides' speed, which is what makes the parallax land on the
    // next frame with nothing to snap.
    //
    // It carries the seasons theme's paper too: that theme has no art, so the
    // month's own colour runs all the way up, and the whole shell follows it so
    // a route transition never flashes something else. Each slide also paints
    // the colour on itself, so two months meet on a visible edge as they pass.
    const syncPaper = () => {
        const { system } = settings.get()
        backdrop.month(calendar.monthAt(slider.getCursor(), system).month, system)
    }
    slider.on('move', ({ dx, ms }: ISliderMove) => backdrop.drag(dx, ms))
    slider.on('change', syncPaper)
    // Which month the slider is resting on, by name rather than by index.
    // Remembered because a day that turns moves what index 0 means, and a user
    // who had swiped away to read another month has to be put back on that
    // month, not on that index. See `rolled` at the bottom.
    let shown = calendar.monthAt(0, settings.get().system)
    const remember = () => { shown = calendar.monthAt(slider.getCursor(), settings.get().system) }
    slider.on('change', remember)
    syncDirection()
    syncPaper()
    slider.fill()

    // The top corners: the menu (hamburger) on the leading (right, in RTL) side,
    // "برو به امروز" opposite it — shown only on the months it would jump from.
    const menu = MenuIcon()
    menu.addClass(style.menu)
    menu.el.onclick = () => router.goto('/menu')
    base.append(menu)

    const todayBtn = Button('برو امروز')
    todayBtn.addClass(style.todayBtn, style.hidden)
    todayBtn.on('click', () => slider.reset())
    slider.on('change', (cursor: number) => todayBtn.toggleClass(style.hidden, cursor === 0))
    base.append(todayBtn)

    // Between the two corners, the prices the reader follows. A tap opens all
    // of them, and the picking.
    const ticker = RatesTicker()
    ticker.addClass(style.ticker)
    ticker.on('click', () => router.goto('/rates'))
    base.append(ticker)

    // Bottom bar: search on the left, the Jalaali ↔ Gregorian switch in the
    // middle, the day/night switch on the right. Three columns rather than
    // spread apart, so the switch sits on the screen's centre and not on the
    // centre of what is left over. The bar itself is transparent to pointers,
    // so a swipe across the gaps still reaches the slider underneath.
    const bar = Div()
    bar.addClass(style.bottomBar)

    // The settings are a page of their own behind the menu; the corner the
    // gear had is the way into search.
    const find = Button()
    find.addClass(style.iconBtn)
    find.attr('aria-label', 'جستجو')
    find.attr('data-ripple', '')
    find.html(SEARCH_SVG)
    find.on('click', () => router.goto('/search'))
    bar.append(find)

    bar.append(CalendarToggle())

    // Walks the five in the order the theme service holds them — the two
    // paintings, the same calendar with the picture taken away, then the
    // month's own color — always showing where it goes next rather than where
    // it is.
    const NEXT: Record<Theme, { icon: string, label: string }> = {
        light: { icon: MOON_SVG, label: 'منظرهٔ شب' },
        dark: { icon: RING_SVG, label: 'زمینهٔ سادهٔ روشن' },
        paper: { icon: DISC_SVG, label: 'زمینهٔ سادهٔ تیره' },
        slate: { icon: PALETTE_SVG, label: 'زمینهٔ رنگی' },
        seasons: { icon: SUN_SVG, label: 'منظرهٔ روز' },
    }
    const themeBtn = Button()
    themeBtn.addClass(style.iconBtn)
    themeBtn.attr('data-ripple', '')
    // The icon has a box of its own: swapping it on a tap would otherwise
    // take the ripple that tap just started away with it.
    const themeIcon = Div()
    themeBtn.append(themeIcon)
    const syncTheme = () => {
        backdrop.theme() // the new theme's art, or none in the three without
        const next = NEXT[settings.get().theme]
        themeBtn.attr('aria-label', next.label)
        themeIcon.html(next.icon)
    }
    syncTheme()
    themeBtn.on('click', () => theme.toggle())
    bar.append(themeBtn)

    base.append(bar)

    // The one-time note about other countries' holidays, once the month is on
    // screen: the first thing to see is the calendar, not a sheet about a
    // setting. Only ever once — see country-intro.
    if (countryIntroDue()) {
        setTimeout(() => {
            if (!countryIntroDue()) return
            const intro = CountryIntro()
            intro.on('close', () => intro.remove())
            base.append(intro)
        }, 1500)
    }

    // Route transition: leaving home (→ /menu and the rest) slides it up and
    // hides it while the next page rises from the bottom; returning brings it
    // back down. The year is the exception — it waits above the calendar, so
    // home travels the other way and the two move together, like one long strip.
    // The day is the other exception: the calendar does not move at all. On a
    // phone the day comes up over it as a sheet, and on a wide screen it opens
    // beside it — see pages/day.
    const EXIT_CLASS: Record<string, string> = { '/year': style.exitingDown }
    base.on('exit', ({ to }: IRouteEvent) => {
        if (to !== '/day') base.addClass(EXIT_CLASS[to || ''] || style.exiting)
    })
    // The year page names a month to come back to (?m=, a slider index) when the
    // user taps one of its cards. Not on a back navigation, where the same query
    // is still in the URL but the calendar is already where the user left it.
    base.on('enter', ({ query, isBack }: IRouteEvent) => {
        base.removeClass(style.exiting, style.exitingDown)
        const m = Number(query?.m)
        if (!isBack && Number.isInteger(m)) slider.goto(m)
    })
    // And when the day's sheet opens a page of its own — a task, a note — the
    // calendar under it makes way with it, the two as one page: the same slide
    // up and out, and back down when that page goes. See leave in pages/day.
    const onDayAway = (away: boolean) => base.toggleClass(style.exiting, away)

    // The day view: a day open in a sheet over the calendar (pages/day). The
    // sheet says which day as the reader moves from day to day, and null once
    // it has gone down. Under it the month rises a little and rings the day,
    // the sheet stops just under the day's row, and that row becomes a line of
    // days to scroll sideways — see lift in MonthSlide. The calendar holds its
    // month meanwhile: a swipe would take it away from under the sheet, and so
    // would the jump to today, which stands aside.
    //
    // The reader may have been down in the month's lists; the month goes back
    // to its top so the day's row is in view, and back to their place when the
    // sheet goes, if it is still the same month.
    let resume: { index: number, top: number } | null = null
    const visible = () => [...months].find(([slide]) => slide.getPos() === 1)?.[1]
    const onDayView = (c: IDayCell | null) => {
        const band = visible()?.el.querySelector<HTMLElement>('[data-band]')
        if (c && !dayView && band) {
            resume = { index: slider.getCursor(), top: band.scrollTop }
            band.scrollTo({ top: 0, behavior: 'smooth' })
        }
        dayView = c
        base.toggleClass(style.dayView, !!c)
        slider.lock(!!c)
        if (c) {
            // A swipe in the sheet, or a tap on the line, can carry the day into
            // another month. The calendar goes with it, so the page around the
            // sheet is that month's colour too, and the fill lays the new
            // month's line out from the same first day, so the line stays put.
            const { system } = settings.get()
            const index = system === 'gregorian'
                ? calendar.monthIndex(c.gy, c.gm, system)
                : calendar.monthIndex(c.jy, c.jm, system)
            if (index !== slider.getCursor()) {
                slider.goto(index) // and the fill raises the new month
                return
            }
        }
        placeSheet(visible()?.lift(c))
        if (!c && resume) {
            if (resume.index === slider.getCursor()) band?.scrollTo({ top: resume.top, behavior: 'smooth' })
            resume = null
        }
    }

    // Any settings change re-renders the three slides in place; so does a
    // moon-sighting correction arriving from the server, which moves hijri day
    // numbers and the occasions anchored to them — and so does a task added or
    // ticked off on the day page, which today's section here has to catch up to.
    // An occasion the user saves or deletes puts a dot on its day, or takes it
    // away, so that redraws the slides too — and so does a batch of the phone's
    // own events arriving, which dots the days they fall on and fills the list
    // under the grid.
    const refill = () => {
        syncDirection()
        syncPaper()
        syncTheme()
        slider.fill()
        remember()
    }

    // Midnight, with the calendar open. Index 0 means today's month, and today
    // has moved — so a user resting there is carried along with the date and
    // sees the new month arrive, which is the point. A user who had swiped away
    // to read some other month is kept on it: the same month now answers to a
    // different index, so it is looked up again by name.
    const rolled = () => {
        const browsing = slider.getCursor() === 0 ? null : shown
        refill()
        if (browsing) slider.goto(calendar.monthIndex(browsing.year, browsing.month, settings.get().system))
    }

    state.on('settings', refill)
    state.on('hijri', refill)
    state.on('tasks', refill)
    state.on('custom-occasions', refill)
    state.on('phone-events', refill)
    state.on('phone-reminders', refill)
    state.on('events', refill)
    // A country the user added lands asynchronously — the catalog is fetched —
    // so the month's list redraws when it arrives, and again when a country or
    // a single day is added or dropped.
    state.on('country-occasions', refill)
    // A name given or an occasion put away on the day page: the month's own
    // list says the same thing the day does.
    state.on('occasion-overrides', refill)
    // And a colour given to a day, or taken off it, on the day page.
    state.on('day-colors', refill)
    state.on('today', rolled)
    state.on('day-view', onDayView)
    state.on('day-view-away', onDayAway)
    base.on('unmounted', () => {
        state.off('settings', refill)
        state.off('hijri', refill)
        state.off('tasks', refill)
        state.off('custom-occasions', refill)
        state.off('phone-events', refill)
        state.off('phone-reminders', refill)
        state.off('events', refill)
        state.off('country-occasions', refill)
        state.off('occasion-overrides', refill)
        state.off('day-colors', refill)
        state.off('today', rolled)
        state.off('day-view', onDayView)
        state.off('day-view-away', onDayAway)
    })

    return base
}
